import { useEffect, useState } from "react";
import api from "../api/api";

type ComputaxStylePayrollForm = {
  deduction: boolean;
  carryForward: boolean;
  daysAllowable: number;
  hourly: boolean;
  monthly: boolean;
  sunday: boolean;
  saturday: boolean;
  holiday_dates: string[];
  lateDeduction: boolean;
  overtimeDeduction: boolean;
  rateofOvertime?: number;
};

const PayrollSetting = () => {
  const [form, setForm] = useState<ComputaxStylePayrollForm>({
    deduction: false,
    carryForward: false,
    daysAllowable: 0,
    hourly: false,
    monthly: true,
    sunday: true,
    saturday: false,
    holiday_dates: [],
    lateDeduction: false,
    overtimeDeduction: false,
    rateofOvertime: 0.0,
  });

  const [holidayInput, setHolidayInput] = useState("");
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const clearMessage = () => {
    setMessage("");
    setIsSuccess(false);
  };

  // Fetch initial data on page load
  const fetchSettings = async () => {
    try {
      setIsInitialLoading(true);
      const response = await api.get("/salary/get_salary_settings");

      if (response.data.success) {
        const apiData = response.data.data;
        setForm({
          deduction: apiData.deduction || false,
          carryForward: apiData.carryForward || false,
          daysAllowable: apiData.daysAllowable || 0,
          hourly: apiData.hourly || false,
          monthly: apiData.monthly || false,
          sunday: apiData.sunday || false,
          saturday: apiData.saturday || false,
          holiday_dates: apiData.holiday_dates || [],
          lateDeduction: apiData.lateDeduction || false,
          overtimeDeduction: apiData.overtimeDeduction || false,
          rateofOvertime: apiData.rateofOvertime || 1.0,
        });
      }
    } catch (error) {
      console.error("Failed to fetch salary settings:", error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleHourlyMonthlyChange = (checked: boolean, isHourly: boolean) => {
    setForm((prev) => ({
      ...prev,
      hourly: isHourly ? checked : false,
      monthly: isHourly ? false : checked,
    }));
  };

  const handleDeductionsChange = (checked: boolean, isDeduction: boolean) => {
    setForm((prev) => ({
      ...prev,
      deduction: isDeduction ? checked : false,
      carryForward: isDeduction ? false : checked,
    }));
  };

  const handleLateDeductionsChange = (checked: boolean, isLateDeduction: boolean) => {
    setForm((prev) => ({
      ...prev,
      lateDeduction: isLateDeduction ? checked : false,
      overtimeDeduction: isLateDeduction ? false : checked,
      rateofOvertime:
        !isLateDeduction && checked
          ? prev.rateofOvertime || 0
          : 0,
    }));
  };

  const handleAddHoliday = () => {
    if (!holidayInput) return;
    if (!form.holiday_dates.includes(holidayInput)) {
      setForm((prev) => ({
        ...prev,
        holiday_dates: [...prev.holiday_dates, holidayInput],
      }));
    }
    setHolidayInput("");
  };

  const handleRemoveHoliday = (date: string) => {
    setForm((prev) => ({
      ...prev,
      holiday_dates: prev.holiday_dates.filter((d) => d !== date),
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      await api.post("/salary/create_or_update_salary_settings", form);
      setMessage("Successfully saved settings!");
      setIsSuccess(true);
      fetchSettings();
      console.log("Form Data:", form);
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.detail || "Failed to save settings";
      setMessage(errorMsg);
      setIsSuccess(false);
      console.log("Error:", errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Beautiful Loading Screen - FORM KI EXACT WIDTH
  if (isInitialLoading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white shadow-xl rounded-2xl p-4">
          <div className="flex flex-col items-center space-y-6 py-12">
            {/* Animated Settings Icon */}
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-emerald-400 to-blue-500 rounded-2xl shadow-xl flex items-center justify-center">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center animate-pulse">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
              </div>
              {/* Bouncing Dot */}
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full animate-bounce"></div>
            </div>

            {/* Loading Text */}
            <div className="text-center space-y-2">
              <div className="text-xl font-bold text-gray-700">
                Loading Settings
              </div>
              <div className="flex justify-center space-x-1">
                <div
                  className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>

            {/* Compact Skeleton */}
            <div className="w-full space-y-3 opacity-60">
              <div className="h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl animate-pulse"></div>
              <div className="h-32 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl animate-pulse border"></div>
              <div className="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white shadow-xl rounded-2xl">
      <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">
        Leave & Holiday Settings
      </h1>

      {/* Leave Policy - SINGLE LINE */}
      <div className="mb-8 p-6 border border-green-200 rounded-2xl bg-green-50 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-green-800">Leave Policy</h2>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.deduction}
                onChange={(e) =>
                  handleDeductionsChange(e.target.checked, true)
                }
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Deduction</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.carryForward}
                onChange={(e) =>
                  handleDeductionsChange(e.target.checked, false)
                }
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Carry Forward</span>
          </label>

          {/* MUTUALLY EXCLUSIVE: Hourly vs Monthly */}
          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.hourly}
                onChange={(e) =>
                  handleHourlyMonthlyChange(e.target.checked, true)
                }
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Hourly</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.monthly}
                onChange={(e) =>
                  handleHourlyMonthlyChange(e.target.checked, false)
                }
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Monthly</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.sunday}
                onChange={(e) => setForm({ ...form, sunday: e.target.checked })}
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Sunday</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.saturday}
                onChange={(e) =>
                  setForm({ ...form, saturday: e.target.checked })
                }
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Saturday</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 hover:scale-105 transition-all cursor-pointer text-sm">
            <div className="w-5 h-5 bg-green-100 rounded-lg flex items-center justify-center">
              <input
                type="checkbox"
                checked={form.lateDeduction}
                onChange={(e) =>
                  handleLateDeductionsChange(e.target.checked, true)
                }
                className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
              />
            </div>
            <span className="font-semibold text-gray-800">Late Deduction</span>
          </label>

          <label className="flex items-center gap-3 p-3 border rounded-xl bg-white hover:shadow-md transition-all text-sm">
            <input
              type="checkbox"
              checked={form.overtimeDeduction}
              onChange={(e) =>
                handleLateDeductionsChange(e.target.checked, false)
              }
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />

            <span className="font-semibold text-gray-800">
              Overtime Deduction
            </span>

            {/* Rate input – only when overtime enabled */}
            {form.overtimeDeduction && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-gray-500">Rate</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.rateofOvertime}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rateofOvertime:
                        e.target.value === ""
                          ? 0
                          : parseFloat(e.target.value),
                    })
                  }
                  placeholder="1.5"
                  className="w-20 px-2 py-1 border rounded-md text-sm focus:ring-2 focus:ring-green-400"
                />
              </div>
            )}
          </label>

        </div>
      </div>

      {/* Days Allowable */}
      <div className="mb-8">
        <label className="block text-lg font-bold mb-3 text-gray-800">
          Days Allowable
        </label>
        <input
          type="number"
          className="w-full p-4 border-2 border-indigo-200 rounded-2xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 bg-indigo-50 shadow-lg text-xl font-bold text-center"
          value={form.daysAllowable}
          onChange={(e) =>
            setForm({ ...form, daysAllowable: Number(e.target.value) || 0 })
          }
          min="0"
        />
      </div>

      {/* Holiday Dates */}
      <div className="mb-8 p-6 border border-blue-200 rounded-2xl bg-blue-50 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-blue-800">Holiday Dates</h2>
        <div className="flex gap-3 mb-4">
          <input
            type="date"
            className="flex-1 p-3 border-2 border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white shadow-sm"
            value={holidayInput}
            onChange={(e) => setHolidayInput(e.target.value)}
          />
          <button
            onClick={handleAddHoliday}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 shadow-md hover:shadow-lg transition-all"
          >
            Add Date
          </button>
        </div>

        <div
          className="max-h-32 overflow-y-auto space-y-1 pr-1 scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-blue-100"
          style={{ scrollSnapType: "y mandatory" }}
        >
          {form.holiday_dates.map((date) => (
            <div
              key={date}
              className="flex items-center justify-between p-2 pl-3 bg-white border border-blue-200 rounded-lg shadow-sm hover:shadow-md hover:scale-[1.02] transition-all snap-start"
              style={{ minHeight: "36px" }}
            >
              <span className="font-mono text-sm font-semibold text-gray-800 truncate">
                {date}
              </span>
              <button
                onClick={() => handleRemoveHoliday(date)}
                className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-lg hover:bg-red-600 shadow-sm transition-all ml-2"
              >
                Remove
              </button>
            </div>
          ))}
          {form.holiday_dates.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="text-2xl mb-1">📅</div>
              <p className="text-sm">No holidays added yet</p>
            </div>
          )}
        </div>
        <div className="mt-2 text-xs text-blue-700 font-medium">
          Total: {form.holiday_dates.length} holidays
        </div>
      </div>

      {/* ERROR/SUCCESS MESSAGE - SAVE BUTTON KE UPAR */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-xl shadow-lg text-base font-semibold text-center flex items-center justify-center ${isSuccess
              ? "bg-green-100 border-2 border-green-300 text-green-800"
              : "bg-red-100 border-2 border-red-300 text-red-800"
            }`}
        >
          {message}
          <button
            onClick={clearMessage}
            className="ml-3 text-lg hover:text-black"
          >
            ×
          </button>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className={`w-80 py-4 px-8 font-bold text-lg rounded-2xl shadow-xl border-2 transition-all ${isLoading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-emerald-500 to-blue-600 text-white hover:shadow-2xl hover:from-emerald-600 hover:to-blue-700 hover:-translate-y-1"
            }`}
        >
          {isLoading ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
};

export default PayrollSetting;
